import Card from 'components/card';
import InputField from 'components/fields/InputField';
import { useEffect, useState, useRef, useMemo} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    deleteProduct,
    duplicateProduct,
    getProduct,
    updateProduct,
    getProducts,
    searchProductsForSelect,
    archiveProduct,
    getCatalogCategories,
    getVarieties,
    getSpecificities,
} from 'interfaces/products';
import { useDisclosure } from '@chakra-ui/hooks';
import StockEntryModal from '../stockList/components/StockEntryModal';
import StockExitModal from '../stockList/components/StockExitModal';
import DeleteModal from 'components/actions/Delete';
import ProductImageWidget from './components/ProductImageWidget';
import GoBack from 'components/actions/GoBack';
import mobilityImg from 'assets/img/mobility.png';
import SaveStatus from 'components/actions/SaveStatus';
import ProductCommandSettings from './components/ProductCommandSettings';
import { GoDuplicate, GoInfo } from 'react-icons/go';
import SearchSelector from 'components/fields/SearchSelector';
import { productColorList } from 'utils/lib';
import ProductMenu from './components/Menu';
import { getTags } from "interfaces/tagProduct";
import { getZones } from "interfaces/zone";
import { getSuppliers, addProductToSupplier ,removeProductFromSupplier } from 'interfaces/supplier';
import { updateInventoryItem } from 'interfaces/stockMovement';
import { MdEdit, MdCheck, MdClose, MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { toast } from 'react-toastify';
import ChildProductsManager from './components/childProducts';
import PackagingsManager from './components/PackagingsManager';
import ProductBarcode from 'components/dataDisplay/Barcode';
import Checkbox from 'components/checkbox';
import PriceHistoryWidget from './components/PriceHistoryWidget';
import CustomPricingWidget from './components/CustomPricingWidget';

const UpdateProduct = () => {
    const navigate = useNavigate();
    const params = useParams();
    // The custom-pricing widget hits /pricing/*, gated by the 'pricing'
    // permission. An account without it (e.g. Production) would otherwise
    // get an unprompted "Vous n'avez pas la permission..." toast the moment
    // this page loads, from a background fetch for a section they were
    // never trying to use — reads as if editing the product itself failed.
    const canManagePricing = useMemo(() => {
        const storedPermissions = JSON.parse(localStorage.getItem('permissions') || '[]') as string[];
        const isOwner = !localStorage.getItem('ownerId');
        return isOwner || storedPermissions.includes('all') || storedPermissions.includes('pricing');
    }, []);
    const [product, setProduct] = useState<any>(null);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [isTagMenuOpen, setIsTagMenuOpen] = useState(false);
    const [saveStatus, setSaveStatus] = useState<any>('none');
    const [duplicating, setDuplicating] = useState(false);
    const [page, setPage] = useState('product');
    const [loaded, setLoaded] = useState(false);
    const [tags, setTags] = useState([]);
    const [zones, setZones] = useState<any[]>([]);
    const [catalogCategories, setCatalogCategories] = useState<string[]>([]);
    const [varieties, setVarieties] = useState<string[]>([]);
    const [specificities, setSpecificities] = useState<string[]>([]);
    const [suppliers, setSuppliers] = useState<any[]>([]);
    const [openSuppliers, setOpenSuppliers] = useState(false);
    const [allProducts, setAllProducts] = useState<any[]>([]);
    const [childProductsIds, setChildProductsIds] = useState<string[]>([]);
    const [loadingChildren, setLoadingChildren] = useState(false);
    const [editingInventoryId, setEditingInventoryId] = useState<string | null>(null);
    const [editInventoryValues, setEditInventoryValues] = useState({
        quantity: '',
        dlc: '',
        area: 'zone-a',
        lotNumber: '',
    });
    const [savingInventoryId, setSavingInventoryId] = useState<string | null>(null);
    const savingRef = useRef(false);
    const lastSavedHash = useRef<string>('');
    const {
        isOpen: isOpenExit,
        onOpen: onOpenExit,
        onClose: onCloseExit,
    } = useDisclosure();
    const {
        isOpen: isOpenDelete,
        onOpen: onOpenDelete,
        onClose: onCloseDelete,
    } = useDisclosure();

    const handleChange = (key: string, value: string | any) => {
        setSaveStatus('saving');
        setProduct((prev: any) => ({ ...prev, [key]: value }));
    };

    const normalizeProduct = (p: any) => {
        const tagId =
          typeof p?.productTag === 'object' && p.productTag !== null
            ? p.productTag._id
            : p?.productTag || p?.tagId || '';
        const suppliers = Array.isArray(p?.suppliers) ? p.suppliers : [];
        const childProducts = Array.isArray(p?.childProducts)
          ? p.childProducts.map((c: any) => (typeof c === 'string' ? c : c?._id))
          : [];
        const replacementProducts = Array.isArray(p?.replacementProducts)
          ? p.replacementProducts.map((c: any) => (typeof c === 'string' ? c : c?._id))
          : [];
        return {
            ...p,
            tagId,
            productTag: tagId,
            suppliers,
            childProducts,
            replacementProducts,
            hideWhenOutOfStock: p?.hideWhenOutOfStock || false,
            showUnitPrice: p?.showUnitPrice || false,
            noDlc: p?.noDlc || false,
            cotisationInterfel: p?.cotisationInterfel || false,
            cotisationInterfelPercentage: p?.cotisationInterfelPercentage || '',
            agricultureLabel: p?.agricultureLabel || '',
            showProducerOnShop: p?.showProducerOnShop || false,
        };
    };

    const initProduct = async (id: string) => {
        const res: any = await getProduct(id);
        if (!res) {
            return;
        }
        const normalized = normalizeProduct(res);
        setProduct(normalized);
        lastSavedHash.current = hashProduct(normalized);
    };

    const hashProduct = (p: any) => {
        if (!p) return '';
        const watched = {
            name: p.name,
            description: p.description,
            ref: p.ref,
            unity: p.unity,
            price: p.price,
            purchasePrice: p.purchasePrice,
            vat: p.vat,
            origin: p.origin,
            accountingCode: p.accountingCode,
            ean: p.ean,
            tagId: p.tagId,
            suppliers: Array.isArray(p.suppliers) ? [...p.suppliers].sort() : [],
            vatPurchase: p.vatPurchase,
            alert: p.alert,
            daysOfConservation: p.daysOfConservation,
            dlcAlert: p.dlcAlert,
            colorCode: p.colorCode,
            productCategory: p.productCategory,
            productSubCategory: p.productSubCategory,
            productSubFamily: p.productSubFamily,
            catalogCategory: p.catalogCategory,
            variety: p.variety,
            specificity: p.specificity,
            brutWeight: p.brutWeight,
            netWeight: p.netWeight,
            subUnity: p.subUnity,
            subUnitQuantity: p.subUnitQuantity,
            childProducts: Array.isArray(p.childProducts) ? [...p.childProducts].sort() : [],
            quantityInPack: p.quantityInPack,
            category: p.category,
            hiddenFromShop: p.hiddenFromShop || false,
            replacementProducts: Array.isArray(p.replacementProducts)
                ? [...p.replacementProducts].sort()
                : [],
            hideWhenOutOfStock: p.hideWhenOutOfStock,
            packagings: Array.isArray(p.packagings) ? p.packagings : [],
            showUnitPrice: p.showUnitPrice,
            noDlc: p.noDlc || false,
            recommendedPrice: p.recommendedPrice,
            cotisationInterfel: p.cotisationInterfel || false,
            cotisationInterfelPercentage: p.cotisationInterfelPercentage,
            agricultureLabel: p.agricultureLabel,
            showProducerOnShop: p.showProducerOnShop || false,
        };
        return JSON.stringify(watched);
    };



    const initTags = async () => {
        try {
            const res: any = await getTags();
            if (res && Array.isArray(res)) {
                setTags(res);
            } else if (res && res.data && Array.isArray(res.data)) {
                setTags(res.data);
            } else {
                setTags([]);
            }
        } catch (error) {
            console.error('Erreur lors du chargement des tags:', error);
            setTags([]);
        }
    };

    const initZones = async () => {
        try {
            const res: any = await getZones();
            if (res && Array.isArray(res)) {
                setZones(res);
            } else if (res && res.data && Array.isArray(res.data)) {
                setZones(res.data);
            } else {
                setZones([]);
            }
        } catch (error) {
            console.error('Erreur lors du chargement des zones:', error);
            setZones([]);
        }
    };

    const initCatalogCategories = async () => {
        const res = await getCatalogCategories();
        setCatalogCategories(Array.isArray(res) ? res : []);
    };

    const initVarieties = async () => {
        const res = await getVarieties();
        setVarieties(Array.isArray(res) ? res : []);
    };

    const initSpecificities = async () => {
        const res = await getSpecificities();
        setSpecificities(Array.isArray(res) ? res : []);
    };

    const initSuppliers = async () => {
        try {
            const res: any = await getSuppliers();
            if (res && Array.isArray(res)) setSuppliers(res);
            else if (res && res.data && Array.isArray(res.data)) setSuppliers(res.data);
            else setSuppliers([]);
        } catch (e) {
            setSuppliers([]);
        }
    };

    const handleChildrenChange = (childIds: string[]) => {
        setSaveStatus('saving');
        setProduct({ ...product, childProducts: childIds });
    };

    const refreshData = async () => {
        initProduct(params.id);
    };

    const refreshProductData = async () => {
        const res = await getProduct(params.id);
        if (res) {
            setProduct(normalizeProduct(res));
        }
    };

    const startEditInventory = (item: any) => {
        setEditingInventoryId(item._id);
        setEditInventoryValues({
            quantity: item.quantity ?? '',
            dlc: product?.noDlc ? '' : (item.dlc ? item.dlc.split('T')[0] : ''),
            area: item.area || 'zone-a',
            lotNumber: item.lotNumber || '',
        });
    };

    const cancelEditInventory = () => {
        setEditingInventoryId(null);
    };

    const saveEditInventory = async () => {
        if (!product?._id || !editingInventoryId) return;

        const originalItem = (product.inventory || []).find((i: any) => i._id === editingInventoryId);
        const wasNegative = (originalItem?.quantity ?? 0) < 0;

        const quantityValue = Number(editInventoryValues.quantity);
        if (editInventoryValues.quantity === '' || isNaN(quantityValue)) {
            toast.error('Quantité invalide');
            return;
        }
        if (quantityValue < 0 && !wasNegative) {
            toast.error("La quantité ne peut pas devenir négative pour une ligne qui n'était pas déjà en rupture de stock");
            return;
        }

        const dlcValue = product?.noDlc ? null : (editInventoryValues.dlc || null);
        // Non-bloquant : une correction légitime de saisie doit rester possible même
        // si la nouvelle DLC est dans le passé (comparaison en chaîne 'YYYY-MM-DD'
        // pour éviter les pièges de fuseau horaire d'un parsing `new Date(...)`).
        if (dlcValue && dlcValue < new Date().toISOString().split('T')[0]) {
            toast.warn('Attention : la DLC saisie est déjà dépassée.');
        }

        setSavingInventoryId(editingInventoryId);
        const res: any = await updateInventoryItem(product._id, editingInventoryId, {
            quantity: quantityValue,
            area: editInventoryValues.area,
            dlc: dlcValue,
            lotNumber: editInventoryValues.lotNumber,
        });
        setSavingInventoryId(null);
        // En cas d'échec, errorHandler (interfaces/utils.tsx) affiche déjà un toast —
        // on garde volontairement la ligne en mode édition pour permettre une
        // correction immédiate (ex. doublon détecté) sans tout reprendre.
        if (res?.error) return;
        setEditingInventoryId(null);
        await refreshProductData();
    };

    const loadAllProducts = async () => {
        setLoadingChildren(true);
        try {
            const res: any = await getProducts(1, 500);
            let products = [];
            if (res) {
                if (Array.isArray(res)) products = res;
                else if (res.data && Array.isArray(res.data)) products = res.data;
                else if (res.meta && res.data) products = res.data;
            }
            setAllProducts(products);
        } catch (e) {
            console.error("Error loading all products", e);
            setAllProducts([]);
        }
        setLoadingChildren(false);
      };
      useEffect(() => { loadAllProducts(); }, []);

      const sortedProducts = useMemo(() => {
        return [...allProducts].sort((a: any, b: any) =>
            (a?.name || '').localeCompare(b?.name || '', 'fr', { sensitivity: 'base' }),
        );
      }, [allProducts]);

      const currentProductIndex = useMemo(() => {
        if (!product?._id) return -1;
        return sortedProducts.findIndex((p: any) => p._id === product._id);
      }, [sortedProducts, product?._id]);

      const previousProduct = currentProductIndex > 0 ? sortedProducts[currentProductIndex - 1] : null;
      const nextProduct =
        currentProductIndex >= 0 && currentProductIndex < sortedProducts.length - 1
            ? sortedProducts[currentProductIndex + 1]
            : null;

      const goToProduct = (id: string) => {
        if (!id) return;
        navigate(`/update-product/${id}`);
      };

      useEffect(() => {
        if (product?.childProducts) {
          const ids = product.childProducts.map((c: any) =>
            typeof c === 'string' ? c : c?._id,
          );
          setChildProductsIds(ids);
        } else {
          setChildProductsIds([]);
        }
      }, [product?.childProducts]);


    const handleSubmit = async () => {
        if (!product || savingRef.current) return;
        const currentHash = hashProduct(product);
        if (currentHash === lastSavedHash.current) return;
        setSaveStatus('saving');
        savingRef.current = true;
      
        try {
          const newProduct = {
            name: product.name,
            unity: product.unity,
            alert: Number(product.alert),
            _id: product._id,
            ref: product.ref,
            quantity: product.quantity,
            price: product.price === undefined || product.price === null || product.price === ''
                ? product.price
                : Number(product.price),
            subUnity: product.subUnity,
            subUnitQuantity: Number(product.subUnitQuantity),
            daysOfConservation: product.daysOfConservation,
            dlcAlert: product.dlcAlert,
            vat: Number(product.vat),
            description: product.description,
            category: product.category,
            hiddenFromShop: product.hiddenFromShop || false,
            brutWeight: product.brutWeight,
            netWeight: product.netWeight,
            purchasePrice: product.purchasePrice === undefined || product.purchasePrice === null || product.purchasePrice === ''
                ? product.purchasePrice
                : Number(product.purchasePrice),
            colorCode: product.colorCode,
            origin: product.origin,
            productTag: product?.tagId || null,
            suppliers: Array.isArray(product.suppliers) ? product.suppliers : [],
            vatPurchase: Number(product.vatPurchase) || 0,
            accountingCode: product.accountingCode || '',
            ean: product.ean || '',
            productCategory: product.productCategory || '',
            productSubCategory: product.productSubCategory || '',
            productSubFamily: product.productSubFamily || '',
            catalogCategory: product.catalogCategory || '',
            variety: product.variety || '',
            specificity: product.specificity || '',
            childProducts: Array.isArray(product.childProducts) ? product.childProducts : [],
            quantityInPack: product.quantityInPack || 0,
            packagings: Array.isArray(product.packagings)
                ? product.packagings.map(({ showOnShop, ...rest }: any) => rest)
                : undefined,
            hideWhenOutOfStock: product.hideWhenOutOfStock || false,
            replacementProducts: Array.isArray(product.replacementProducts) ? product.replacementProducts : [],
            showUnitPrice: product.showUnitPrice || false,
            noDlc: product.noDlc || false,
            recommendedPrice: product.recommendedPrice || null,
            cotisationInterfel: product.cotisationInterfel || false,
            cotisationInterfelPercentage: product.cotisationInterfelPercentage || null,
            agricultureLabel: product.agricultureLabel || '',
            showProducerOnShop: product.showProducerOnShop || false,
          };
      
          await updateProduct(newProduct);
      
          const currentSupplierIds = Array.isArray(product.suppliers) ? product.suppliers : [];
          const previousSupplierIds = product.previousSuppliers || []; // store previous suppliers in state
      
          const addedSuppliers = currentSupplierIds.filter((id: any) => !previousSupplierIds.includes(id));
          const removedSuppliers = previousSupplierIds.filter((id: any) => !currentSupplierIds.includes(id));
      
          for (const supplierId of addedSuppliers) {
            await addProductToSupplier(supplierId, product._id);
          }
      
          for (const supplierId of removedSuppliers) {
            await removeProductFromSupplier(supplierId, product._id);
          }
      
          setProduct((prev: any) => ({ ...prev, previousSuppliers: currentSupplierIds }));
          lastSavedHash.current = currentHash;
          setSaveStatus('saved');
                    } catch (err) {
                    console.error('Erreur lors de la mise à jour du produit:', err);
          setSaveStatus('error');
        } finally {
            savingRef.current = false;
        }
    };
      

    const duplicate = async () => {
        setDuplicating(true);
        const res = await duplicateProduct(product._id);
        if (res) {
            setDuplicating(false);
            window.open(`/update-product/${res._id}`, '_blank');
        }
    };

    const delProduct = async (id: string) => {
        const res = await archiveProduct(id);
        // On failure, errorHandler (interfaces/utils.ts) already surfaced a toast — nothing more to do here.
        if (res && !res.error) {
            navigate('/stock/stock-list');
        }
    };

    const getSelectedTag = () => {
        const currentTagId =
          product?.tagId ||
          (typeof product?.productTag === 'object' ? product?.productTag?._id : product?.productTag);
        if (!currentTagId || !Array.isArray(tags) || tags.length === 0) return null;
        return tags.find((tag: any) => tag._id === currentTagId);
      };

    const getFormattedTags = () => {
        return tags.map((tag: any) => ({
            _id: tag._id,
            name: tag.name,
            color: tag.color,
            displayName: tag.name 
        }));
    };

    const handleTagChange = (tagId: string) => {
        setSaveStatus('saving');
        setProduct({ ...product, tagId: tagId });
    };

    const getFormattedCatalogCategories = () => {
        return catalogCategories.map((cat: string) => ({
            _id: cat,
            name: cat,
            displayName: cat,
        }));
    };

    const handleCatalogCategoryCreate = (newCategory: string) => {
        if (!catalogCategories.includes(newCategory)) {
            setCatalogCategories([...catalogCategories, newCategory].sort((a, b) => a.localeCompare(b)));
        }
    };

    const getFormattedVarieties = () => {
        return varieties.map((v: string) => ({
            _id: v,
            name: v,
            displayName: v,
        }));
    };

    const handleVarietyCreate = (newVariety: string) => {
        if (!varieties.includes(newVariety)) {
            setVarieties([...varieties, newVariety].sort((a, b) => a.localeCompare(b)));
        }
    };

    const getFormattedSpecificities = () => {
        return specificities.map((s: string) => ({
            _id: s,
            name: s,
            displayName: s,
        }));
    };

    const handleSpecificityCreate = (newSpecificity: string) => {
        if (!specificities.includes(newSpecificity)) {
            setSpecificities([...specificities, newSpecificity].sort((a, b) => a.localeCompare(b)));
        }
    };

    const supplierIds: string[] = Array.isArray(product?.suppliers) ? product.suppliers : [];
    const toggleSupplier = async (id: string) => {
        if (!product) return;
        const next = supplierIds.includes(id)
            ? supplierIds.filter((s) => s !== id)
            : [...supplierIds, id];
        setSaveStatus('saving');
        setProduct({ ...product, suppliers: next });
    };

    useEffect(() => {
        if (!product) return;
        if (!loaded) {
            setLoaded(true);
            lastSavedHash.current = hashProduct(product);
            return;
        }
        const currentHash = hashProduct(product);
        if (currentHash === lastSavedHash.current) return;
        const timer = setTimeout(() => {
            handleSubmit();
        }, 2000);
        return () => clearTimeout(timer);
    }, [product, loaded]);

    useEffect(() => {
        if (localStorage.getItem('token') === null) {
            navigate('/auth/login');
        }
        initTags();
        initZones();
        initSuppliers();
        initCatalogCategories();
        initVarieties();
        initSpecificities();
        const queryParameters = new URLSearchParams(window.location.search);
        const page = queryParameters.get('action');
        if (page && page === 'entry') {
            onOpen();
        }
    }, []);

    useEffect(() => {
        if (!params.id) return;
        setSaveStatus('none');
        setEditingInventoryId(null);
        initProduct(params.id);
    }, [params.id]);

    const getMarginString = () => {
        if (!product?.purchasePrice && product?.price) {
            const purchasePrice = Number(product?.marginData?.purchasePrice) || 0;
            const price = Number(product?.price) || 0;
            const margin = price - purchasePrice;
            const percentage = (margin / price) * 100;
            return `Marge : ${margin.toFixed(2)}€ (${percentage.toFixed(
                2,
            )} %), Coût total : ${purchasePrice.toFixed(2)} €, Prix de vente : ${price.toFixed(2)} €`;
        } else if (!product?.purchasePrice) {
            const marginData = product?.marginData;
            return `Marge : ${Number(marginData?.margin).toFixed(2)}€ (${Number(marginData?.percentage).toFixed(2)} %), Coût total : ${Number(marginData?.purchasePrice).toFixed(2)} €, Prix de vente : ${Number(marginData?.sellPrice).toFixed(2)} €`;
        } else {
            const purchasePrice = Number(product.purchasePrice) || 0;
            const price = Number(product.price) || 0;
            const margin = price - purchasePrice;
            const percentage = (margin / price) * 100;
            return `Marge : ${margin.toFixed(2)}€ (${percentage.toFixed(
                2,
            )} %), Coût total : ${purchasePrice.toFixed(2)} €, Prix de vente : ${
                price.toFixed(2)
            } €`;
        }
    };

    return (
        <>
            <div className="mt-1 flex justify-between">
                <GoBack text="Liste des produits" />
                <SaveStatus status={saveStatus} />
            </div>

            <StockEntryModal
                onClose={onClose}
                isOpen={isOpen}
                stock={product ? [product] : []}
                onDone={refreshData}
            />
            <StockExitModal
                onClose={onCloseExit}
                isOpen={isOpenExit}
                stock={product ? [product] : []}
                onDone={refreshData}
            />
            
            <div className="flex items-center gap-4 mt-4 mb-2">
                <h2 className='text-2xl font-bold text-navy-700 dark:text-white'>
                    {product?.name?.toUpperCase() || ''}
                </h2>
                <span className={`px-4 py-1 rounded-full text-sm font-bold shadow-sm ${
                    product?.quantity <= (product?.alert || 0) 
                    ? 'bg-red-100 text-red-600 dark:bg-red-500/20 dark:text-red-400 border border-red-200 dark:border-red-500/30' 
                    : 'bg-green-100 text-green-600 dark:bg-green-500/20 dark:text-green-400 border border-green-200 dark:border-green-500/30'
                }`}>
                    Stock : {product?.quantity} {product?.unity}
                </span>
            </div>
            
            <div className="grid grid-cols-12 gap-3">
                <Card extra={`col-span-12 sm:col-span-6 mt-5 px-5 border py-5 ${isTagMenuOpen || openSuppliers ? '!z-20' : ''}`}>
                    <div className="grid grid-cols-2 gap-3">
                        <h4 className="mb-[20px] text-lg font-medium">
                            {' '}
                            Produit
                        </h4>
                        {product && product?.type !== 'brut' && (
                            <div className="col-span-2 col-start-8">
                                <div className="flex gap-2">
                                    <button
                                        className="linear flex cursor-pointer items-center justify-center rounded-xl border border-[#01499b] px-2 py-2 text-xs text-[#01499b] transition duration-200 hover:bg-brand-600/5 active:bg-red-100 dark:active:bg-red-50 dark:active:opacity-80"
                                        onClick={() =>
                                            navigate(
                                                `/handle-comp/${product?._id}`,
                                            )
                                        }
                                    >
                                        Modifier les composants
                                    </button>
                                    <button
                                        className="linear flex cursor-pointer items-center justify-center rounded-xl border border-[#01499b] px-2 py-2 text-xs text-[#01499b] transition duration-200 hover:bg-brand-600/5 active:bg-red-100 dark:active:bg-red-50 dark:active:opacity-80"
                                        onClick={duplicate}
                                        disabled={duplicating}
                                    >
                                        Dupliquer
                                        <GoDuplicate
                                            size={15}
                                            className="ml-2 text-[#01499b]"
                                        />
                                    </button>
                                </div>
                            </div>
                        )}
                        {product && product?.type === 'brut' && (
                            <div className="col-span-2 col-start-8">
                                <button
                                    className="linear flex cursor-pointer items-center justify-center rounded-xl border border-[#01499b] px-2 py-2 text-xs text-[#01499b] transition duration-200 hover:bg-brand-600/5 active:bg-red-100 dark:active:bg-red-50 dark:active:opacity-80"
                                    onClick={duplicate}
                                    disabled={duplicating}
                                >
                                    Dupliquer
                                    <GoDuplicate
                                        size={15}
                                        className="ml-2 text-[#01499b]"
                                    />
                                </button>
                            </div>
                        )}
                        <div className="col-span-2 col-start-10">
                            <button
                                className="linear flex cursor-pointer items-center justify-center rounded-xl border border-red-500 px-2 py-2 text-xs text-red-500 transition duration-200 hover:bg-red-50 active:bg-red-100 dark:active:bg-red-50 dark:active:opacity-80"
                                onClick={onOpenDelete}
                            >
                                Archiver
                            </button>
                        </div>
                    </div>
                    <ProductMenu
                        path={page}
                        onPageChange={(p: any) => setPage(p)}
                    />
                    {page === 'product' && (
                        <div className="mt-4 flex gap-3">
                            <p className="text-sm">{getMarginString()}</p>
                            <span title="La marge est calculé à partir du prix d'achat du produit, s'il n'est pas renseigné, la marge sera calculée à partir du prix d'achat des composants.">
                                <GoInfo className="hover:cursor-pointer" />
                            </span>
                        </div>
                    )}
                    <DeleteModal
                        isOpen={isOpenDelete}
                        onClose={onCloseDelete}
                        description="Voulez-vous archiver ce produit ?"
                        context="produit"
                        archived={true}
                        deleteFunction={() => {
                            delProduct(product?._id);
                        }}
                    />

                    <div className="col-span-10 mb-2 mt-3 grid grid-cols-3 gap-3">
                    {page === 'product' && (
  <>
    <InputField
      label="Produit"
      placeholder="Chargement..."
      id="product-name"
      type="text"
      value={product?.name || ''}
      onChange={(e: any) => {
        handleChange('name', e.target.value);
      }}
      required={true}
      isLoaded={product}
      inputSmall={true}
    />

    <InputField
      label="Référence"
      placeholder="Chargement..."
      id="product-ref"
      type="text"
      value={product?.ref || ''}
      onChange={(e: any) => {
        handleChange('ref', e.target.value);
      }}
      inputSmall={true}
    />

    <InputField
      label="Unité"
      placeholder="Chargement..."
      id="product-unity"
      type="text"
      value={product?.unity || ''}
      onChange={(e: any) => {
        handleChange('unity', e.target.value);
      }}
      inputSmall={true}
    />

    <InputField
      label="Prix de vente (€)"
      placeholder="0.00"
      id="product-price"
      type="number"
      value={product?.price || ''}
      onChange={(e: any) => {
        handleChange('price', e.target.value);
      }}
      inputSmall={true}
    />

    <InputField
      label="Prix d'achat (€)"
      placeholder="0"
      id="purchase-price"
      type="number"
      value={product?.purchasePrice || ''}
      onChange={(e: any) => {
        handleChange('purchasePrice', e.target.value);
      }}
      inputSmall={true}
    />

    <InputField
      label="PVC - Prix vente conseillé"
      placeholder="0.00"
      id="recommended-price"
      type="number"
      value={product?.recommendedPrice || ''}
      onChange={(e: any) => {
        handleChange('recommendedPrice', e.target.value);
      }}
      inputSmall={true}
    />

    <InputField
      label="TVA (%)*"
      placeholder="20"
      id="vat"
      type="number"
      value={product?.vat || ''}
      onChange={(e: any) => {
        handleChange('vat', e.target.value);
      }}
      required={true}
      inputSmall={true}
      isLoaded={product}
    />

    <InputField
      label="Origine"
      placeholder="France, Espagne..."
      id="origin"
      type="text"
      value={product?.origin || ''}
      onChange={(e: any) => {
        handleChange('origin', e.target.value);
      }}
      inputSmall={true}
    />

        <InputField
            label="Code comptable"
            placeholder="701000"
            id="accountingCode"
            type="string"
            value={product?.accountingCode || ''}
            onChange={(e: any) => {
                handleChange('accountingCode', e.target.value);
            }}
            inputSmall={true}
        />

        <div className="flex flex-col gap-2">
            <InputField
                label="Code EAN"
                placeholder="12345678"
                id="ean"
                type="text"
                value={product?.ean || ''}
                onChange={(e: any) => {
                    // Numbers, letters, dashes, dots, and spaces are allowed for EAN-128
                    const val = e.target.value.replace(/[^0-9A-Za-z\-\.\s]/g, '').slice(0, 128);
                    handleChange('ean', val);
                }}
                inputSmall={true}
            />
            {product?.ean && (
                <div className="flex flex-col gap-1">
                    <div className="flex justify-center items-center">
                        <ProductBarcode ean={product?.ean} />
                    </div>
                    <p className="text-xs text-center text-gray-500 italic">
                        {/^\d{8}$/.test(product.ean) 
                            ? 'Format: EAN-8' 
                            : /^\d{13}$/.test(product.ean)
                            ? 'Format: EAN-13 (France)' 
                            : product.ean.length >= 13 
                            ? 'Format: EAN-128 (International)'
                            : 'Code incomplet ou invalide'
                        }
                    </p>
                </div>
            )}
        </div>

    <div className="z-1">
      <SearchSelector
        label="Tag pour production"
        displayKey="name"
        options={getFormattedTags()}
        value={product?.tagId || ''}
        onSelect={(selectedTag: any) => {
          handleTagChange(selectedTag ? selectedTag._id : '');
        }}
        defaultKey="_id"
        canCreate={false}
        inputSmall={true}
        onMenuStateChange={setIsTagMenuOpen}
      />
    </div>

    {getSelectedTag() && (
      <div className="col-span-3">
        <label className="text-gray-600 text-sm mb-2 block">
          Tag sélectionné
        </label>
        <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
          <div
            className="w-4 h-4 rounded-full border-2 border-white shadow-sm"
            style={{ backgroundColor: getSelectedTag()?.color }}
          ></div>
          <span className="text-gray-900 font-medium text-sm flex-1">
            {getSelectedTag()?.name}
          </span>
          <button
            onClick={() => handleTagChange('')}
            className="text-red-500 hover:text-red-700 text-xs px-2 py-1 rounded hover:bg-red-50 transition-colors"
            title="Supprimer le tag"
          >
            ✕
          </button>
        </div>
      </div>
    )}
  </>
)}

                        {page === 'shop-btob' && (
                            <>
                                {/* Packagings Manager */}
                                <div className="col-span-3">
                                    <PackagingsManager
                                        packagings={product?.packagings}
                                        onChange={(packagings) => handleChange('packagings', packagings)}
                                        baseUnity={product?.unity || ''}
                                        basePrice={product?.price || 0}
                                        baseRef={product?.ref || ''}
                                    />
                                </div>
                                <div className="col-span-12 mt-4 border-t pt-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Checkbox
                                            checked={
                                                product?.hideWhenOutOfStock ||
                                                false
                                            }
                                            onChange={(e: any) =>
                                                handleChange(
                                                    'hideWhenOutOfStock',
                                                    e.target.checked,
                                                )
                                            }
                                        />
                                        <p className="text-sm font-medium text-navy-700 dark:text-white">
                                            Masquer ce produit sur le shop B2B lorsqu'il n'y a plus de stock
                                        </p>
                                    </div>

                                    {product?.hideWhenOutOfStock && (
                                        <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
                                            <div className="mb-3">
                                                <label className="text-sm font-bold text-navy-700 dark:text-white">
                                                    Produits de remplacement
                                                </label>
                                                <p className="text-xs text-gray-500">
                                                    Si le produit est masqué, ces produits seront proposés à la place.
                                                </p>
                                            </div>

                                            <div className="grid grid-cols-12 gap-4">
                                                <div className="col-span-12 md:col-span-6">
                                                    <SearchSelector
                                                        label="Ajouter un produit de remplacement"
                                                        options={allProducts.filter(
                                                            (p) =>
                                                                p._id !== product._id &&
                                                                !(product.replacementProducts || []).includes(p._id),
                                                        )}
                                                        onSearch={async (term: string) => {
                                                            const results = await searchProductsForSelect(term);
                                                            const excluded = new Set([product._id, ...(product.replacementProducts || [])]);
                                                            return results.filter((p: any) => !excluded.has(p._id));
                                                        }}
                                                        displayKey="name"
                                                        value={null}
                                                        onSelect={(e: any) => {
                                                            const current = Array.isArray(product?.replacementProducts)
                                                                ? product.replacementProducts
                                                                : [];
                                                            handleChange('replacementProducts', [...current, e._id]);
                                                        }}
                                                        canCreate={false}
                                                        createNew={() => {}}
                                                        inputSmall={true}
                                                    />
                                                </div>
                                                <div className="col-span-12">
                                                    <div className="flex flex-wrap gap-2 mt-2">
                                                        {(
                                                            product?.replacementProducts ||
                                                            []
                                                        ).map((id: string) => {
                                                            const prod =
                                                                allProducts.find(
                                                                    (p) =>
                                                                        p._id ===
                                                                        id,
                                                                );
                                                            return (
                                                                <div
                                                                    key={id}
                                                                    className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-3 py-1"
                                                                >
                                                                    <span className="text-sm text-navy-700">
                                                                        {prod?.name ||
                                                                            'Produit inconnu'}
                                                                    </span>
                                                                    <button
                                                                        onClick={() => {
                                                                            const current =
                                                                                Array.isArray(
                                                                                    product?.replacementProducts,
                                                                                )
                                                                                    ? product.replacementProducts
                                                                                    : [];
                                                                            handleChange(
                                                                                'replacementProducts',
                                                                                current.filter(
                                                                                    (
                                                                                        i: string,
                                                                                    ) =>
                                                                                        i !==
                                                                                        id,
                                                                                ),
                                                                            );
                                                                        }}
                                                                        className="text-gray-400 hover:text-red-500 font-bold ml-1"
                                                                    >
                                                                        x
                                                                    </button>
                                                                </div>
                                                            );
                                                        })}
                                                        {(
                                                            product?.replacementProducts ||
                                                            []
                                                        ).length === 0 && (
                                                            <p className="text-xs text-gray-400 italic my-auto">
                                                                Aucun produit de
                                                                remplacement
                                                                sélectionné
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}

                        {page === 'stock' && (
                            <>
                                <InputField
                                    label="Seuil d'alerte"
                                    placeholder="0"
                                    id="name"
                                    type="number"
                                    value={product?.alert || ''}
                                    onChange={(e: any) => {
                                        handleChange('alert', e.target.value);
                                    }}
                                    inputSmall={true}
                                />
                                <InputField
                                    label="DLC (jours)"
                                    placeholder="0"
                                    id="daysOfConservation"
                                    type="number"
                                    disabled={product?.noDlc || false}
                                    value={product?.daysOfConservation || ''}
                                    onChange={(e: any) => {
                                        handleChange(
                                            'daysOfConservation',
                                            e.target.value,
                                        );
                                    }}
                                    inputSmall={true}
                                />
                                <InputField
                                    label="Alerte DLC proche (jours)"
                                    placeholder="0"
                                    id="dlcAlert"
                                    type="number"
                                    disabled={product?.noDlc || false}
                                    value={product?.dlcAlert || ''}
                                    onChange={(e: any) => {
                                        handleChange(
                                            'dlcAlert',
                                            e.target.value,
                                        );
                                    }}
                                    inputSmall={true}
                                />
                                <div className="flex items-center gap-2 mt-2">
                                    <Checkbox
                                        id="noDlc"
                                        checked={product?.noDlc || false}
                                        onChange={(e: any) => {
                                            const checked = e.target.checked;
                                            setSaveStatus('saving');
                                            setProduct((prev: any) => ({
                                                ...prev,
                                                noDlc: checked,
                                                ...(checked ? { daysOfConservation: 0, dlcAlert: 0 } : {}),
                                            }));
                                        }}
                                    />
                                    <label
                                        htmlFor="noDlc"
                                        className="text-sm font-bold text-navy-700 dark:text-white select-none cursor-pointer"
                                    >
                                        Sans DLC
                                        <p className="mt-0.5 text-xs font-normal text-gray-500">
                                            Aucune date de péremption
                                        </p>
                                    </label>
                                </div>
                                <div className="col-span-3">
                                    <label className="text-navy-700 text-xs font-medium">
                                        Fournisseurs
                                    <p className="mt-1 text-xs text-gray-600">
                                        Modifier ou ajouter les fournisseurs liés à ce produit.
                                    </p>
                                    </label>

                                <div className="relative mt-2">
                                    <button
                                        type="button"
                                        onClick={() => setOpenSuppliers((o) => !o)}
                                        className="flex w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-sm text-gray-700 hover:border-toola-500 focus:outline-none"
                                    >
                                {supplierIds.length === 0
                                        ? 'Aucun fournisseur sélectionné'
                                        : `${supplierIds.length} fournisseur(s) sélectionné(s)`}
                                            <span className="text-xs text-gray-400">
                                                {openSuppliers ? '▲' : '▼'}
                                            </span>
                                    </button>

                            {openSuppliers && (
                                <div className="absolute z-20 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
                            {suppliers.length === 0 && (
                                    <p className="p-2 text-xs text-gray-500">
                                        Aucun fournisseur disponible
                                    </p>
                            )}
                            {suppliers.map((s: any) => (
                                <label
                                    key={s._id}
                                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-gray-50"
                                >
                                <input
                                    type="checkbox"
                                    className="h-4 w-4 accent-toola-500"
                                    checked={supplierIds.includes(s._id)}
                                    onChange={() => toggleSupplier(s._id)}
                                />
                                    <span className="text-gray-700">
                                        {s.displayName || s.company}
                                    </span>
                                </label>
                            ))}
                        </div>
                        )}
                    </div>

    {supplierIds.length > 0 && (
      <div className="mt-2 flex flex-wrap gap-2">
        {supplierIds.map((id) => {
          const sup = suppliers.find((s: any) => s._id === id);
          return (
            <span
              key={id}
              className="flex items-center gap-1 rounded-full bg-toola-500/10 px-2 py-1 text-xs text-toola-600"
            >
              {sup?.displayName || sup?.company}
              <button
                type="button"
                onClick={() => toggleSupplier(id)}
                className="text-toola-500 hover:text-toola-700"
              >
                ✕
              </button>
            </span>
          );
        })}
      </div>
    )}
    <div className="mt-2 flex items-center gap-2 rounded-lg bg-yellow-50 p-3 border border-yellow-200">
        <Checkbox
            checked={product?.showProducerOnShop || false}
            onChange={(e: any) =>
                handleChange('showProducerOnShop', e.target.checked)
            }
        />
        <p className="text-sm font-medium text-navy-700 dark:text-white">
            Afficher le(s) fournisseur(s) comme "Producteur" sur la boutique B2B
        </p>
    </div>
  </div>
                        <div className="col-span-6 lg:col-span-3 ml-2 mt-2">
                        <InputField
                            label="TVA fournisseur (%)"
                            placeholder="5.5"
                            id="vatPurchase"
                            type="number"
                            value={product?.vatPurchase || ''}
                            onChange={(e: any) => {
                                handleChange('vatPurchase', e.target.value);
                            }}
                            inputSmall={true}
                        />
                    </div>
                            </>
                        )}
                        {page === 'tech' && (
                            <>
                                <div className="col-span-3 mb-2 rounded-lg bg-blue-50 p-3 text-sm text-blue-800">
                                    <p className="font-medium">
                                        Comment définir les quantités et
                                        sous-unités ?
                                    </p>
                                    <p className="mt-1">
                                        Elles servent d'indications pour vos
                                        prix (1€/kg) en plus de votre prix
                                        produit. Par exemple, si vous vendez par
                                        pack de 15 Kg, la quantité serait 15 et
                                        la sous-unité serait "Kg".
                                    </p>
                                </div>
                                <div className="col-span-3 mb-3 flex items-center gap-2 rounded-lg bg-yellow-50 p-3 border border-yellow-200">
                                    <Checkbox
                                        checked={product?.showUnitPrice || false}
                                        onChange={(e: any) =>
                                            handleChange('showUnitPrice', e.target.checked)
                                        }
                                    />
                                    <div className="flex-1">
                                        <p className="text-sm font-medium text-navy-700 dark:text-white">
                                            Afficher le prix unitaire sur les documents
                                        </p>
                                        <p className="text-xs text-gray-600 mt-1">
                                            {product?.subUnitQuantity && product?.subUnity
                                                ? `Le prix unitaire sera calculé : ${product.price}€ ÷ ${product.subUnitQuantity} = ${(product.price / product.subUnitQuantity).toFixed(2)}€/${product.subUnity}`
                                                : 'Renseignez la quantité de sous-unité et la sous-unité pour activer cette option'}
                                        </p>
                                    </div>
                                </div>
                                <InputField
                                    label="Quantité de sous-unité"
                                    placeholder="0"
                                    id="name"
                                    type="number"
                                    value={product?.subUnitQuantity || ''}
                                    onChange={(e: any) => {
                                        handleChange(
                                            'subUnitQuantity',
                                            e.target.value,
                                        );
                                    }}
                                    inputSmall={true}
                                />
                                <InputField
                                    label="Sous-Unité"
                                    placeholder="Kg, L..."
                                    id="unity"
                                    type="text"
                                    value={product?.subUnity || ''}
                                    onChange={(e: any) => {
                                        handleChange(
                                            'subUnity',
                                            e.target.value,
                                        );
                                    }}
                                    inputSmall={true}
                                />
                                <InputField
                                    label="Poids brut (Kg)"
                                    placeholder="0"
                                    id="brutWeight"
                                    type="number"
                                    value={product?.brutWeight || ''}
                                    onChange={(e: any) => {
                                        handleChange(
                                            'brutWeight',
                                            e.target.value,
                                        );
                                    }}
                                    inputSmall={true}
                                />
                                <InputField
                                    label="Poids net (Kg)"
                                    placeholder="0"
                                    id="netWeight"
                                    type="number"
                                    value={product?.netWeight || ''}
                                    onChange={(e: any) => {
                                        handleChange(
                                            'netWeight',
                                            e.target.value,
                                        );
                                    }}
                                    inputSmall={true}
                                />
                                <InputField
                                    label="Quantité contenu dans pack"
                                    placeholder="0"
                                    id="quantityInPack"
                                    type="number"
                                    value={product?.quantityInPack || ''}
                                    onChange={(e: any) => {
                                        handleChange(
                                            'quantityInPack',
                                            e.target.value,
                                        );
                                    }}
                                    inputSmall={true}
                                />
                                {!isOpen && !isOpenExit && (
                                    <div className="z-10">
                                        <SearchSelector
                                            label="Code couleur"
                                            displayKey="name"
                                            options={productColorList}
                                            value={product?.colorCode}
                                            onSelect={(e: any) => {
                                                handleChange('colorCode', e.id);
                                            }}
                                            defaultKey="id"
                                            canCreate={false}
                                            createNew={onOpen}
                                            inputSmall={true}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                        {page === 'perso' && (
                        <>
                        <InputField
                                    label="Catégorie du produit ou calibre (sur facture)"
                                    placeholder="Chargement..."
                                    id="productCategory"
                                    type="string"
                                    value={product?.productCategory || ''}
                                    onChange={(e: any) => {
                                        handleChange(
                                            'productCategory',
                                            e.target.value,
                                        );
                                    }}
                                    inputSmall={true}
                                />
                                <div className="mt-4 max-w-xs">
                                    <SearchSelector
                                        label="Catégorie produit"
                                        displayKey="name"
                                        options={getFormattedCatalogCategories()}
                                        value={product?.catalogCategory || ''}
                                        onSelect={(selected: any) => {
                                            handleChange('catalogCategory', selected?.name || '');
                                        }}
                                        defaultKey="_id"
                                        canCreate={false}
                                        onCreateOption={handleCatalogCategoryCreate}
                                        inputSmall={true}
                                    />
                                </div>
                                <div className="mt-4 max-w-xs">
                                    <SearchSelector
                                        label="Variété"
                                        displayKey="name"
                                        options={getFormattedVarieties()}
                                        value={product?.variety || ''}
                                        onSelect={(selected: any) => {
                                            handleChange('variety', selected?.name || '');
                                        }}
                                        defaultKey="_id"
                                        canCreate={false}
                                        onCreateOption={handleVarietyCreate}
                                        inputSmall={true}
                                    />
                                </div>
                                <div className="mt-4 max-w-xs">
                                    <SearchSelector
                                        label="Spécificité"
                                        displayKey="name"
                                        options={getFormattedSpecificities()}
                                        value={product?.specificity || ''}
                                        onSelect={(selected: any) => {
                                            handleChange('specificity', selected?.name || '');
                                        }}
                                        defaultKey="_id"
                                        canCreate={false}
                                        onCreateOption={handleSpecificityCreate}
                                        inputSmall={true}
                                    />
                                </div>
                                <div className="mt-4 max-w-xs">
                                    <InputField
                                        label="Libellé agriculture"
                                        placeholder="Agriculture biologique..."
                                        id="agricultureLabel"
                                        type="text"
                                        value={product?.agricultureLabel || ''}
                                        onChange={(e: any) => {
                                            handleChange(
                                                'agricultureLabel',
                                                e.target.value,
                                            );
                                        }}
                                        inputSmall={true}
                                    />
                                </div>
                                <div className="mt-4 flex items-center">
                                    <Checkbox
                                        id="cotisationInterfel"
                                        checked={product?.cotisationInterfel || false}
                                        onChange={(e: any) => {
                                            const checked = e.target.checked;
                                            handleChange('cotisationInterfel', checked);
                                            if (!checked) {
                                                handleChange('cotisationInterfelPercentage', '');
                                            }
                                        }}
                                    />
                                    <label
                                        htmlFor="cotisationInterfel"
                                        className="ml-2 text-sm font-bold text-navy-700 dark:text-white"
                                    >
                                        Cotisation interfel
                                    </label>
                                </div>
                                {product?.cotisationInterfel && (
                                    <div className="mt-4 max-w-xs">
                                        <InputField
                                            label="Pourcentage de commission (%)"
                                            placeholder="0"
                                            id="cotisationInterfelPercentage"
                                            type="number"
                                            value={product?.cotisationInterfelPercentage || ''}
                                            onChange={(e: any) => {
                                                handleChange(
                                                    'cotisationInterfelPercentage',
                                                    e.target.value,
                                                );
                                            }}
                                            inputSmall={true}
                                        />
                                    </div>
                                )}
                                <div className="col-span-3 mt-4">
                                {product && (
                                    <ChildProductsManager
                                        productId={product._id}
                                        childProductIds={childProductsIds}
                                        allProducts={allProducts}
                                        onChildrenChange={handleChildrenChange}
                                        isLoading={loadingChildren}
                                        parentProduct={product.parentProduct}
                                    />
                                )}
                                </div>
                            </>
                        )}
                    </div>
                </Card>
                <div className="col-span-12 sm:col-span-6">
                    {product &&
                        <ProductImageWidget
                            productId={params?.id}
                            imageUrl={product?.imageUrl}
                            imageUrls={product?.imageUrls || []}
                            onImagesUpdate={refreshProductData}
                        />
                    }
                </div>
                <Card extra="!z-1 col-span-12 sm:col-span-6 px-[30px] pt-5 pb-[40px] flex flex-col border">
                    <div className="flex justify-between">
                        <h4 className="mb-2 text-lg font-medium">
                            {' '}
                            Gestion de stock
                        </h4>
                        <div className="flex gap-2">
                            <div>
                                <button
                                    className="text-xs rounded-xl border-2 border-[#0260CB] px-2 py-1 font-medium text-[#0260CB] transition duration-200 hover:bg-brand-600/5 active:bg-brand-700/5 dark:border-brand-400 dark:bg-brand-400/10 dark:text-white dark:hover:bg-brand-300/10 dark:active:bg-brand-200/10"
                                    onClick={onOpen}
                                >
                                    Entrer du stock
                                </button>
                            </div>
                            <div>
                                <button
                                    className="text-xs rounded-xl border-2 border-[#0260CB] px-2 py-1 font-medium text-[#0260CB] transition duration-200 hover:bg-brand-600/5 active:bg-brand-700/5 dark:border-brand-400 dark:bg-brand-400/10 dark:text-white dark:hover:bg-brand-300/10 dark:active:bg-brand-200/10"
                                    onClick={onOpenExit}
                                >
                                    Sortir du stock
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="col-span-12 mt-5 grid grid-cols-12 gap-5">
                        <div className="col-span-2">
                            <p className="text-xs font-bold md:text-sm">Quantité</p>
                        </div>
                        <div className="col-span-3">
                            <p className="text-xs font-bold md:text-sm">DLC</p>
                        </div>
                        <div className="col-span-3">
                            <p className="text-xs font-bold md:text-sm">Zone</p>
                        </div>
                        <div className="col-span-2">
                            <p className="text-xs font-bold md:text-sm">Lot</p>
                        </div>
                        <div className="col-span-2" />
                        {product &&
                            product.inventory &&
                            product.inventory
                                .filter((i: any) => i.quantity !== 0)
                                .map((item: any) => {
                                    const isEditing = editingInventoryId === item._id;
                                    return (
                                        <div
                                            key={item._id}
                                            className="col-span-12 mt-4 grid grid-cols-12 items-center gap-5"
                                        >
                                            <div className="col-span-2">
                                                {isEditing ? (
                                                    <input
                                                        type="number"
                                                        min={item.quantity < 0 ? undefined : 0}
                                                        className="w-full rounded-lg border border-gray-200 px-2 py-1 text-xs outline-none dark:!border-white/10 dark:!bg-navy-800 md:text-sm"
                                                        value={editInventoryValues.quantity}
                                                        onChange={(e: any) =>
                                                            setEditInventoryValues({
                                                                ...editInventoryValues,
                                                                quantity: e.target.value,
                                                            })
                                                        }
                                                    />
                                                ) : (
                                                    <p className="text-xs md:text-sm">
                                                        {item.quantity}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="col-span-3">
                                                {isEditing ? (
                                                    product?.noDlc ? (
                                                        <span className="text-xs italic text-gray-400 md:text-sm">
                                                            Sans DLC
                                                        </span>
                                                    ) : (
                                                        <input
                                                            type="date"
                                                            className="w-full rounded-lg border border-gray-200 px-2 py-1 text-xs outline-none dark:!border-white/10 dark:!bg-navy-800 md:text-sm"
                                                            value={editInventoryValues.dlc}
                                                            onChange={(e: any) =>
                                                                setEditInventoryValues({
                                                                    ...editInventoryValues,
                                                                    dlc: e.target.value,
                                                                })
                                                            }
                                                        />
                                                    )
                                                ) : (
                                                    <p className="text-xs md:text-sm">
                                                        {item.dlc
                                                            ? item.dlc.split('T')[0]
                                                            : 'Aucune DLC'}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="col-span-3">
                                                {isEditing ? (
                                                    <select
                                                        className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs outline-none dark:!border-white/10 dark:!bg-navy-800 md:text-sm"
                                                        value={editInventoryValues.area}
                                                        onChange={(e: any) =>
                                                            setEditInventoryValues({
                                                                ...editInventoryValues,
                                                                area: e.target.value,
                                                            })
                                                        }
                                                    >
                                                        {zones.map((z: any) => (
                                                            <option key={z.slug} value={z.slug}>{z.name}</option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <p className="text-xs md:text-sm">
                                                        {item.area || 'zone-a'}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="col-span-2">
                                                {isEditing ? (
                                                    <input
                                                        type="text"
                                                        className="w-full rounded-lg border border-gray-200 px-2 py-1 text-xs outline-none dark:!border-white/10 dark:!bg-navy-800 md:text-sm"
                                                        value={editInventoryValues.lotNumber}
                                                        onChange={(e: any) =>
                                                            setEditInventoryValues({
                                                                ...editInventoryValues,
                                                                lotNumber: e.target.value,
                                                            })
                                                        }
                                                    />
                                                ) : (
                                                    <p className="text-xs md:text-sm">
                                                        {item.lotNumber || 'Inconnu'}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="col-span-2 flex items-center gap-3">
                                                {isEditing ? (
                                                    <>
                                                        <button
                                                            type="button"
                                                            title="Enregistrer"
                                                            className="text-green-600 hover:text-green-800 disabled:opacity-50"
                                                            disabled={savingInventoryId === item._id}
                                                            onClick={saveEditInventory}
                                                        >
                                                            <MdCheck size={18} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            title="Annuler"
                                                            className="text-gray-400 hover:text-red-500"
                                                            onClick={cancelEditInventory}
                                                        >
                                                            <MdClose size={18} />
                                                        </button>
                                                    </>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        title="Modifier"
                                                        className="text-[#0260CB] hover:text-brand-700"
                                                        onClick={() => startEditInventory(item)}
                                                    >
                                                        <MdEdit size={16} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                        {product &&
                            product.inventory &&
                            product.inventory.filter(
                                (i: any) => i.quantity !== 0,
                            ).length === 0 && (
                                <div className="col-span-12 mt-10">
                                    <img
                                        className="mx-auto flex h-[120px] w-[120px] items-center justify-center"
                                        src={mobilityImg}
                                        alt=""
                                    />
                                    <div className="font-large mt-5 flex items-center justify-center gap-5 rounded-xl">
                                        <button
                                            className="col-span-12 rounded-xl border-2 border-[#0260CB] px-5 py-3 text-base font-medium text-[#0260CB] transition duration-200 hover:bg-brand-600/5 active:bg-brand-700/5 dark:border-brand-400 dark:bg-brand-400/10 dark:text-white dark:hover:bg-brand-300/10 dark:active:bg-brand-200/10 md:col-span-2 xl:col-start-9"
                                            onClick={onOpen}
                                        >
                                            Entrer du stock
                                        </button>
                                        <button
                                            className="col-span-12 rounded-xl border-2 border-[#0260CB] px-5 py-3 text-base font-medium text-[#0260CB] transition duration-200 hover:bg-brand-600/5 active:bg-brand-700/5 dark:border-brand-400 dark:bg-brand-400/10 dark:text-white dark:hover:bg-brand-300/10 dark:active:bg-brand-200/10 md:col-span-2 xl:col-start-11"
                                            onClick={onOpenExit}
                                        
                                                                             >
                                            Sortir du stock
                                        </button>
                                    </div>
                                </div>
                            )}
                    </div>
                </Card>
                <div className="col-span-12 sm:col-span-6">
                    <ProductCommandSettings
                        product={product}
                        handleChange={handleChange}
                    />
                </div>
                {product && product._id && (
                    <PriceHistoryWidget productId={product._id} />
                )}
                {product && product._id && canManagePricing && (
                    <CustomPricingWidget
                        productId={product._id}
                        basePrice={product?.price}
                        purchasePrice={product?.purchasePrice}
                        packagings={product?.packagings}
                    />
                )}
            </div>

            {product && sortedProducts.length > 0 && (
                <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-5 py-3 shadow-sm dark:border-white/10 dark:bg-navy-800">
                    <button
                        type="button"
                        disabled={!previousProduct}
                        onClick={() => goToProduct(previousProduct?._id)}
                        className="linear flex max-w-[40%] items-center gap-1 rounded-xl border border-[#01499b] px-3 py-2 text-xs font-medium text-[#01499b] transition duration-200 hover:bg-brand-600/5 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent"
                    >
                        <MdChevronLeft size={16} className="shrink-0" />
                        <span className="truncate">
                            {previousProduct ? previousProduct.name : 'Produit précédent'}
                        </span>
                    </button>

                    <span className="shrink-0 text-xs text-gray-400">
                        {currentProductIndex >= 0
                            ? `${currentProductIndex + 1} / ${sortedProducts.length}`
                            : ''}
                    </span>

                    <button
                        type="button"
                        disabled={!nextProduct}
                        onClick={() => goToProduct(nextProduct?._id)}
                        className="linear flex max-w-[40%] items-center gap-1 rounded-xl border border-[#01499b] px-3 py-2 text-xs font-medium text-[#01499b] transition duration-200 hover:bg-brand-600/5 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent"
                    >
                        <span className="truncate">
                            {nextProduct ? nextProduct.name : 'Produit suivant'}
                        </span>
                        <MdChevronRight size={16} className="shrink-0" />
                    </button>
                </div>
            )}
        </>
    );
};

export default UpdateProduct;
